import { convertMeasurement, parseMeasurementText, parseMeasurementUnit } from './measurement'

export const csvCell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`
const key = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '')

/** Quoted delimiters, escaped quotes, BOM and multiline cells are supported. */
export function readCsv(text: string): string[][] {
  text = text.replace(/^\uFEFF/, '')
  const counts: Record<string, number> = { ';': 0, '\t': 0, ',': 0 }
  let inside = false
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '"') {
      if (inside && text[i + 1] === '"') i++
      else inside = !inside
    } else if (!inside && text[i] === '\n') break
    else if (!inside && text[i] in counts) counts[text[i]]++
  }
  const delimiter = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0]
  const rows: string[][] = []; let row: string[] = []; let cell = ''; let quoted = false
  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    if (char === '"') {
      if (quoted && text[i + 1] === '"') { cell += '"'; i++ } else quoted = !quoted
    } else if (!quoted && (char === delimiter || char === '\n')) {
      row.push(cell.trim()); cell = ''
      if (char === '\n') { if (row.some(Boolean)) rows.push(row); row = [] }
    } else if (char !== '\r' || quoted) cell += char
  }
  if (quoted) throw new Error('Guillemet CSV non fermé')
  row.push(cell.trim()); if (row.some(Boolean)) rows.push(row)
  return rows
}

export function exchangeNumber(value: unknown, label: string, optional = false): number | null {
  if (value == null || value === '') {
    if (optional) return null
    throw new Error(`${label} : valeur manquante`)
  }
  const parsed = parseMeasurementText(String(value))
  if (parsed.status !== 'valid') throw new Error(`${label} : nombre invalide`)
  return parsed.value
}

export function parseMeasurementCsv(text: string, kind: 'catalog' | 'cargo'): any[] {
  const rows = readCsv(text)
  if (!rows.length) throw new Error('Fichier vide')
  const hasHeader = rows[0].filter(v => /^(sku|nom(?:du)?produit|nom|name|description|(?:longueur|length)[a-z]*)$/.test(key(v))).length >= 2
  const headers = hasHeader ? rows.shift()! : kind === 'catalog'
    ? ['sku', 'nom', 'description', 'longueur', 'largeur', 'hauteur', 'poids', 'adr', 'froid', 'empilable', 'ausol', 'rotatable']
    : ['sku', 'description', 'longueur', 'largeur', 'hauteur', 'poids', 'quantite', 'groupe', 'empilable']
  const names = headers.map(key)
  const errors: string[] = []
  const result = rows.map((row, index) => {
    try {
      const get = (...aliases: string[]) => row[names.findIndex(n => aliases.includes(n))]
      const measure = (aliases: string[], unitColumn: string[], fallback: string, target: 'mm' | 'kg') => {
        const column = names.findIndex(n => aliases.some(a => n.startsWith(a)))
        const alias = aliases.find(a => names[column]?.startsWith(a))
        const suffix = alias ? names[column].slice(alias.length) : ''
        const unit = parseMeasurementUnit(get(...unitColumn) || suffix || fallback)
        if (suffix && get(...unitColumn) && parseMeasurementUnit(suffix) !== unit) throw new Error('Unités contradictoires')
        const value = exchangeNumber(row[column], headers[column] || aliases[0], kind === 'catalog')
        convertMeasurement(1, unit, target)
        if (value != null && (value < 0 || target === 'mm' && value === 0)) throw new Error(`${aliases[0]} : valeur hors limites`)
        return value == null ? null : convertMeasurement(value, unit, target)
      }
      const bool = (value: string | undefined, fallback = false) => value == null || value === '' ? fallback : ['oui', 'true', '1'].includes(value.toLowerCase())
      const quantity = exchangeNumber(get('quantite', 'quantity', 'qte') ?? 1, 'Quantité')!
      if (!Number.isInteger(quantity) || quantity < 1) throw new Error('Quantité : entier positif requis')
      return {
        sku: get('sku', 'code') || '', name: get('nom', 'nomproduit', 'nomduproduit', 'name') || get('sku') || `Produit ${index + 1}`,
        description: get('description') || '',
        length: measure(['longueur', 'length'], ['unitedim', 'dimensionunit'], kind === 'catalog' ? 'cm' : 'mm', 'mm'),
        width: measure(['largeur', 'width'], ['unitedim', 'dimensionunit'], kind === 'catalog' ? 'cm' : 'mm', 'mm'),
        height: measure(['hauteur', 'height'], ['unitedim', 'dimensionunit'], kind === 'catalog' ? 'cm' : 'mm', 'mm'),
        weight: measure(['poids', 'weight'], ['unitepoids', 'weightunit'], 'kg', 'kg'),
        dimension_unit: 'mm', weight_unit: 'kg', quantity,
        keep_together_group: get('groupe', 'group') || undefined,
        stackable: bool(get('empilable', 'stackable'), true), rotatable: bool(get('rotationautorisee', 'rotatable'), true),
        must_be_on_floor: bool(get('ausol', 'ausoluniquement')),
        no_stack_above: bool(get('nepassuperposer')),
        is_dangerous_goods: bool(get('adr', 'matieredangereuseadr')),
        dangerous_goods_class: get('classeadr') || null, un_number: get('nun') || null,
        temperature_controlled: bool(get('froid', 'controletemperature')),
        min_temperature: exchangeNumber(get('tempminc'), 'Température min.', true),
        max_temperature: exchangeNumber(get('tempmaxc'), 'Température max.', true),
      }
    } catch (error) { errors.push(`Ligne ${index + (hasHeader ? 2 : 1)} : ${(error as Error).message}`); return null }
  })
  if (errors.length) throw new Error(errors.join('\n'))
  if (!result.length) throw new Error('Aucune donnée')
  return result
}

export function parseProductJson(text: string): any[] {
  const json = JSON.parse(text)
  return (Array.isArray(json) ? json : [json]).map((item, index) => {
    const result = { ...item, ...item.constraints, ...item.stacking, ...item.pallet_specs }
    result.name = item.name || item.nom || item.sku
    if (!result.name) throw new Error(`Produit ${index + 1} : nom manquant`)
    result.dimension_unit = parseMeasurementUnit(item.dimension_unit ?? item.dimensions?.unit ?? 'cm')
    result.weight_unit = parseMeasurementUnit(item.weight_unit ?? item.weight?.unit ?? 'kg')
    for (const [field, french] of [['length', 'longueur'], ['width', 'largeur'], ['height', 'hauteur']]) {
      result[field] = exchangeNumber(item[field] ?? item[french] ?? item.dimensions?.[field], field, true)
      if (result[field] != null && result[field] <= 0) throw new Error(`${field} : valeur positive requise`)
    }
    result.weight = exchangeNumber(typeof item.weight === 'object' ? item.weight?.value : item.weight ?? item.poids ?? item.masse, 'Poids', true)
    if (result.weight != null && result.weight < 0) throw new Error('Poids négatif')
    const bool = (value: unknown) => typeof value === 'string' ? ['oui', 'true', '1'].includes(value.toLowerCase()) : Boolean(value)
    for (const [field, alias] of [['is_dangerous_goods', 'adr'], ['temperature_controlled', 'froid'], ['must_be_on_floor', 'au_sol']]) {
      if (result[field] != null || item[alias] != null) result[field] = bool(result[field] ?? item[alias])
    }
    if (result.stackable == null && item.empilable != null) result.stackable = bool(item.empilable)
    convertMeasurement(1, result.dimension_unit, 'mm'); convertMeasurement(1, result.weight_unit, 'kg')
    for (const [field, unit, target] of [
      ['max_stack_weight', item.stack_weight_unit ?? 'kg', 'kg'],
      ['pallet_custom_length', item.pallet_dimension_unit ?? 'mm', 'mm'],
      ['pallet_custom_width', item.pallet_dimension_unit ?? 'mm', 'mm'],
      ['max_pallet_height_mm', 'mm', 'mm'], ['pallet_board_height_mm', 'mm', 'mm'],
      ['pallet_empty_weight_kg', 'kg', 'kg'], ['max_pallet_weight_kg', 'kg', 'kg'],
    ] as const) {
      if (result[field] != null) {
        const value = exchangeNumber(result[field], field)!
        if (value < 0) throw new Error(`${field} : valeur négative`)
        result[field] = convertMeasurement(value, parseMeasurementUnit(unit), target)
      }
    }
    if (result.volume != null) {
      result.volume = exchangeNumber(result.volume, 'Volume')
      result.volume_unit = parseMeasurementUnit(result.volume_unit ?? 'm3')
      convertMeasurement(result.volume, result.volume_unit, 'm3')
      if (result.volume < 0) throw new Error('Volume négatif')
    }
    // Only catalog fields are submitted: never import ownership, IDs or JSON wrappers.
    const allowed = new Set(('center_of_gravity_x center_of_gravity_y center_of_gravity_z name description sku hs_code unit_of_measure weight weight_unit length width height dimension_unit volume volume_unit packaging_type is_dangerous_goods dangerous_goods_class un_number temperature_controlled min_temperature max_temperature temperature_unit country_of_origin status quantity stackable fragile rotatable no_stack_above max_stack_weight max_stack_layer color color_group unloading_order keep_upright must_be_on_floor keep_together_group min_support_ratio requires_pallet pallet_type max_pallet_height_mm pallet_custom_length pallet_custom_width max_items_per_pallet pallet_board_height_mm pallet_empty_weight_kg pallet_color max_pallet_weight_kg allow_rotation_x allow_rotation_y allow_rotation_z rotation_strategy favorite_rating').split(' '))
    return Object.fromEntries(Object.entries(result).filter(([field]) => allowed.has(field)))
  })
}
